import { CustomerAddress, CustomerMeResponse } from '../../../core/customer/customer-dtos';

/**
 * D9 — pure presentation helpers for the account area. No PII is ever logged;
 * these only format what's already on screen. Kept framework-free so they're
 * trivially unit-testable.
 */

/** Initials from a name, e.g. "Yasir Khan" → "YK". Falls back to a glyph. */
export function initialsOf(firstName?: string | null, lastName?: string | null): string {
  const a = (firstName ?? '').trim();
  const b = (lastName ?? '').trim();
  const i = (a.charAt(0) + b.charAt(0)).toUpperCase();
  return i || '·';
}

/** Display name, gracefully degrading to "there" when nothing is set. */
export function displayName(me: Pick<CustomerMeResponse, 'firstName' | 'lastName'>): string {
  const full = [me.firstName, me.lastName].map(s => (s ?? '').trim()).filter(Boolean).join(' ');
  return full || 'there';
}

/** Mask a mobile to its last 4 digits: "+971501234567" → "+9715•••4567". */
export function maskMobile(mobile: string): string {
  const m = mobile.trim();
  if (m.length <= 4) return m;
  return m.slice(0, Math.max(0, m.length - 7)).padEnd(m.length - 4, '•') + m.slice(-4);
}

/** Mask an email's local part: "guest@example.com" → "gu•••@example.com". */
export function maskEmail(email: string): string {
  const [user, domain] = email.trim().split('@');
  if (!domain) return email.trim();
  const head = user.length <= 2 ? user : user.slice(0, 2) + '•••';
  return `${head}@${domain}`;
}

/** The contact line shown under the name — mobile preferred, else email. */
export function maskedContact(me: Pick<CustomerMeResponse, 'mobile' | 'email'>): string {
  const mob = (me.mobile ?? '').trim();
  if (mob) return maskMobile(mob);
  const email = (me.email ?? '').trim();
  return email ? maskEmail(email) : '';
}

/** One-line address summary, omitting blank parts. */
export function addressLine(a: CustomerAddress): string {
  return [a.apartmentVilla, a.building, a.street, a.area, a.city]
    .map(s => (s ?? '').trim()).filter(Boolean).join(', ');
}
